import {
  EmployeeHours,
  GetEmployeeWorkingHoursResponse,
  LoginResponseData,
} from '@EHSNavigator/backend';
import clsx from 'clsx';
import React, { useState } from 'react';
import SidebarCrossIcon from '../../assets/icons/sidebar-cross-icon.svg?react';
import { getManageEmployeeWorkingHoursSchema } from './schema';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import Button from '../Button';
import EditIcon from '../../assets/icons/edit-icon.svg?react';
import CheckGreenIcon from '../../assets/icons/check-icon.svg?react';
import Input from '../Input';
import moment from 'moment';
import PlusIcon from '../../assets/icons/add-icon.svg?react';
import Select from '../Select';
import { useButtonSizeSmaller } from '../../utils/buttonSizes';
import WarningIcon from '../../assets/icons/warning-icon.svg?react';
import { useApiClients } from '../../client/api/useApiClient';
import { useLocalStorage } from '@uidotdev/usehooks';
import { queryClient } from '../../main';
import styles from './EmployeeWorkingHoursModal.module.scss';
interface EmployeeWorkingHoursModalProps {
  otherClasses?: string;
  employeeWorkingHours: GetEmployeeWorkingHoursResponse['employeeHours'];
  handleClose: () => void;
}

const EmployeeWorkingHoursModal: React.FC<EmployeeWorkingHoursModalProps> = ({
  otherClasses,
  employeeWorkingHours,
  handleClose,
}) => {
  const employeeWorkingHoursModalClasses = clsx(otherClasses, '');
  const { UserSettingsApi } = useApiClients();
  const [userInfo] = useLocalStorage<LoginResponseData | null>('userInfo');
  const [state, setState] = useState<{
    toggle: boolean;
    selectedCategory: EmployeeHours | null;
  }>({
    toggle: false,
    selectedCategory: null,
  });
  const createEmployeeWorkingHoursFormHandler = useFormik({
    enableReinitialize: true,
    initialValues: {
      year: '',
      hours: '',
    },
    validationSchema: getManageEmployeeWorkingHoursSchema(),
    onSubmit: async (values) => {
      try {
        const res =
          await UserSettingsApi.userSettingsControllerUpdateOrganizationDetails(
            {
              id: userInfo?.user?.org?.id as string,
              updateOrganizationSettingDto: {
                totalHours: Number(values?.hours),
                year: Number(values?.year),
              },
            },
          );
        queryClient.setQueryData(
          ['get-employee-working-hours'],
          (oldData: GetEmployeeWorkingHoursResponse['employeeHours']) => {
            if (!oldData) return [res.data.employeeHours];

            const exists = oldData.some(
              (node) => node.year === res?.data?.employeeHours?.year,
            );

            if (exists) {
              return oldData.map((node) =>
                node.year === res?.data?.employeeHours?.year
                  ? res.data.employeeHours
                  : node,
              );
            }

            return [...oldData, res.data.employeeHours];
          },
        );

        toast.success(res?.data?.message);
        setState((prev) => ({
          ...prev,
          toggle: false,
        }));
        createEmployeeWorkingHoursFormHandler.resetForm();
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });

  const editEmployeeWorkingHoursFormHandler = useFormik({
    enableReinitialize: true,
    initialValues: {
      year: state?.selectedCategory?.year.toString() || '',
      hours: state?.selectedCategory?.totalHours || '',
    },
    validationSchema: getManageEmployeeWorkingHoursSchema(),
    onSubmit: async (values) => {
      try {
        const res =
          await UserSettingsApi.userSettingsControllerUpdateOrganizationDetails(
            {
              id: userInfo?.user?.org?.id as string,
              updateOrganizationSettingDto: {
                totalHours: Number(values?.hours),
                year: Number(values?.year),
              },
            },
          );
        queryClient.setQueryData(
          ['get-employee-working-hours'],
          (oldData: GetEmployeeWorkingHoursResponse['employeeHours']) => {
            if (!oldData) return [res.data.employeeHours]; // bootstrap array

            return oldData.map((node) =>
              node.id === res?.data?.employeeHours?.id
                ? res.data.employeeHours
                : node,
            );
          },
        );
        toast.success(res?.data?.message);
        editEmployeeWorkingHoursFormHandler.resetForm();
        setState((prev) => ({
          ...prev,
          selectedCategory: null,
        }));
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });

  const startYear = 2025;
  const currentYear = moment().year();

  const list: { label: string; value: string }[] = [];

  for (let year = startYear; year <= currentYear; year++) {
    list.push({
      label: year.toString(),
      value: year.toString(),
    });
  }

  const md = useButtonSizeSmaller();

  return (
    <div
      className={employeeWorkingHoursModalClasses}
      data-testid="employee-working-hours-modal"
    >
      <>
        <div className="mb-8 flex w-full items-center justify-between lg:mb-12">
          <div className="flex flex-row items-center gap-2.5">
            <EditIcon className="[&>path]:fill-brand-byzantine h-9 w-9 lg:h-60 lg:w-60" />
            <h4 className="text-brand-byzantine">Annual Working Hours</h4>
          </div>
          <button
            className="bg-card-backgrounds/5 border-neutral-tones-text/35 flex h-9 min-h-9 w-9 min-w-9 cursor-pointer items-center justify-center rounded-full border"
            onClick={handleClose}
          >
            <SidebarCrossIcon className="[&>g>path]:fill-neutral-tones-text h-5 w-5" />
          </button>
        </div>
        <div className="">
          <div
            className={clsx(
              'border-b-neutral-tones-text/35 mb-6 grid items-center gap-6 border-b pb-6',
              styles.gridContainer,
            )}
          >
            <h5 className="text-neutral-tones-text text-xl font-bold">Sr.</h5>
            <h5 className="text-neutral-tones-text text-xl font-bold">Year</h5>
            <h5 className="text-neutral-tones-text text-xl font-bold">Hours</h5>
          </div>
          <div>
            {employeeWorkingHours?.length > 0 ? (
              employeeWorkingHours?.map((node, index) =>
                state?.selectedCategory?.id !== node?.id ? (
                  <div
                    key={index}
                    className={clsx(
                      'border-b-neutral-tones-text/10 mb-6 grid items-center gap-6 border-b pb-6',
                      styles.gridContainer,
                    )}
                  >
                    <p className="font-inter-variable text-neutral-tones-text text-lg leading-5 font-normal">
                      {index + 1}
                    </p>
                    <p className="font-inter-variable text-neutral-tones-text text-lg leading-5 font-normal">
                      {node?.year}
                    </p>

                    <div className="flex items-center justify-between">
                      <p className="font-inter-variable text-neutral-tones-text text-lg leading-5 font-normal">
                        {`${node?.totalHours} hr`}
                      </p>
                      <div className="flex items-center gap-15">
                        <button
                          onClick={() =>
                            setState((prev: any) => ({
                              ...prev,
                              selectedCategory: node,
                            }))
                          }
                          className="cursor-pointer"
                        >
                          <EditIcon className="[&>path]:fill-brand-byzantine h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <form
                    key={index}
                    onSubmit={editEmployeeWorkingHoursFormHandler.handleSubmit}
                    className={clsx(
                      'border-b-neutral-tones-text/10 mb-6 grid gap-6 border-b pb-6',
                      styles.gridContainer,
                    )}
                  >
                    <p className="font-inter-variable text-neutral-tones-text text-lg leading-5 font-normal">
                      {index + 1}
                    </p>
                    <Select
                      filterSelect
                      list={list}
                      value={editEmployeeWorkingHoursFormHandler?.values?.year.toString()}
                      placeholder="Select Year"
                      otherClasses={{
                        container: 'w-full gap-15',
                        button:
                          'pl-0 !pb-2 !pt-0 h-fit !ring-0 border-b border-b-neutral-tones-text rounded-none focus:border-b-brand-byzantine bg-transparent',
                        listContainer: '!bottom-10 !top-auto !p-2 ',
                        list: '!py-1 [&>button]:text-left',
                        label:
                          'text-xs font-bold text-brand-byzantine italic font-inter-variable leading-3.5',
                      }}
                      onSelect={(e: string) =>
                        editEmployeeWorkingHoursFormHandler?.setFieldValue(
                          'year',
                          e,
                        )
                      }
                      errorMessage={
                        editEmployeeWorkingHoursFormHandler?.touched.year &&
                        editEmployeeWorkingHoursFormHandler?.errors.year
                          ? editEmployeeWorkingHoursFormHandler?.errors.year
                          : ''
                      }
                    />
                    <div className="flex items-center justify-between">
                      <Input
                        value={editEmployeeWorkingHoursFormHandler.values.hours}
                        onChange={
                          editEmployeeWorkingHoursFormHandler.handleChange
                        }
                        placeholder="Enter working hours"
                        name="hours"
                        type="number"
                        disabled={
                          editEmployeeWorkingHoursFormHandler.isSubmitting
                        }
                        otherClasses={{
                          container: 'w-full gap-15',
                          label:
                            'text-xs font-bold text-brand-byzantine italic font-inter-variable leading-3.5 ',
                          input:
                            'pl-0 pb-2 pr-0 pt-0 h-fit !ring-0 border-b border-b-neutral-tones-text rounded-none focus:border-b-brand-byzantine',
                        }}
                        errorMessage={
                          editEmployeeWorkingHoursFormHandler?.touched.hours &&
                          editEmployeeWorkingHoursFormHandler?.errors.hours
                            ? editEmployeeWorkingHoursFormHandler?.errors.hours
                            : ''
                        }
                      />
                      <div className="flex items-center gap-2.5">
                        <button
                          type="submit"
                          disabled={
                            editEmployeeWorkingHoursFormHandler.values.year.toString() ===
                              state?.selectedCategory?.year.toString() &&
                            editEmployeeWorkingHoursFormHandler.values.hours ===
                              state?.selectedCategory?.totalHours
                          }
                          className={clsx(
                            'cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
                          )}
                        >
                          <CheckGreenIcon className="[&>path]:fill-brand-papery h-7 w-7" />
                        </button>
                        <button
                          onClick={() => {
                            setState((prev) => ({
                              ...prev,
                              selectedCategory: null,
                            }));
                          }}
                          type="button"
                          className="group cursor-pointer"
                        >
                          <SidebarCrossIcon className="[&>g>path]:fill-neutral-tones-text/50 group-hover:[&>g>path]:fill-brand-posh-pink h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </form>
                ),
              )
            ) : (
              <div className="flex h-full flex-col items-center justify-center">
                <WarningIcon className="[&>g>path]:fill-brand-byzantine mb-4 h-16 w-16" />
                <p className="font-inter-variable text-neutral-tones-text text-lg leading-5 font-normal">
                  No employee working hours found
                </p>
              </div>
            )}
          </div>
          {state?.toggle && (
            <form
              onSubmit={createEmployeeWorkingHoursFormHandler.handleSubmit}
              className="mt-4 flex w-full flex-col justify-between gap-6 sm:flex-row sm:items-center"
            >
              <div className="grid flex-1 grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-15">
                <Select
                  filterSelect
                  list={list}
                  value={createEmployeeWorkingHoursFormHandler?.values?.year}
                  placeholder="Select Year"
                  label="Year"
                  otherClasses={{
                    container: 'w-full gap-15',
                    button:
                      'pl-0 !pb-2 !pt-0 h-fit !ring-0 border-b border-b-neutral-tones-text rounded-none focus:border-b-brand-byzantine bg-transparent',
                    listContainer: '!bottom-12 !top-auto  !p-2 ',
                    list: '!py-1 [&>button]:text-left',
                    label:
                      'text-xs font-bold text-brand-byzantine italic font-inter-variable leading-3.5',
                  }}
                  onSelect={(e: string) =>
                    createEmployeeWorkingHoursFormHandler?.setFieldValue(
                      'year',
                      e,
                    )
                  }
                  errorMessage={
                    createEmployeeWorkingHoursFormHandler?.touched.year &&
                    createEmployeeWorkingHoursFormHandler?.errors.year
                      ? createEmployeeWorkingHoursFormHandler?.errors.year
                      : ''
                  }
                />
                <Input
                  value={createEmployeeWorkingHoursFormHandler.values.hours}
                  onChange={createEmployeeWorkingHoursFormHandler.handleChange}
                  placeholder="Enter working hours"
                  name="hours"
                  type="number"
                  disabled={createEmployeeWorkingHoursFormHandler.isSubmitting}
                  label="Working Hours"
                  otherClasses={{
                    container: 'w-full gap-15',
                    label:
                      'text-xs font-bold text-brand-byzantine italic font-inter-variable leading-3.5 ',
                    input:
                      'pl-0 pb-2  pt-0 h-fit !ring-0 border-b border-b-neutral-tones-text rounded-none focus:border-b-brand-byzantine',
                  }}
                  errorMessage={
                    createEmployeeWorkingHoursFormHandler?.touched.hours &&
                    createEmployeeWorkingHoursFormHandler?.errors.hours
                      ? createEmployeeWorkingHoursFormHandler?.errors.hours
                      : ''
                  }
                />
              </div>
              <div className="flex w-full flex-col items-center gap-2.5 sm:w-auto sm:flex-row">
                <Button
                  type="submit"
                  variant="primary"
                  size="smaller"
                  otherClasses="w-full sm:w-24"
                >
                  Save
                </Button>
                <Button
                  onClick={() => {
                    setState((prev) => ({ ...prev, toggle: !prev.toggle }));
                    createEmployeeWorkingHoursFormHandler.resetForm();
                  }}
                  type="button"
                  variant="secondary"
                  otherClasses="text-brand-posh-pink w-full sm:w-24 hover:text-brand-posh-pink hover:border-brand-posh-pink"
                  size="smaller"
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
          <div
            className={clsx(
              'flex flex-col gap-2.5',
              employeeWorkingHours?.length > 0 ? '' : 'mt-4 items-center',
            )}
          >
            {!state?.toggle && (
              <Button
                onClick={() =>
                  setState((prev) => ({ ...prev, toggle: !prev.toggle }))
                }
                size={md}
                variant="primary"
                otherClasses="w-full md:w-fit"
              >
                <PlusIcon className="h-5 w-5 [&>path]:fill-current" /> Add
                Employee Working Hours
              </Button>
            )}
          </div>
        </div>
      </>
    </div>
  );
};

export default EmployeeWorkingHoursModal;
